import { createSelector } from "reselect";

import { INGESTION_SOURCE_DATA_MAP, MANUAL_SOURCE } from 'constants/ingestion';
import type { IIngestionSourceData } from 'constants/ingestion/types';
import { INTERNATIONAL_SHIPPING_RETAILERS_NAMES } from "constants/retailer-ids";
import { STATUS_TYPES } from "constants/status-types";
import { userNames } from "shared/user-helpers";

export const getUser = state => state.user || {};

/** @return {MRAPI.User} */
export function getUserData(state) {
  return getUser(state).data || {};
}

export function getUserDataId(state) {
  return getUserData(state).id || {};
}

export function getUserDataStatus(state) {
  return getUser(state).status;
}

export function getUserEmail(state) {
  return getUser(state).data.email || {};
}

export function getIsUserLoaded(state) {
  return getUserDataStatus(state) === STATUS_TYPES.LOADED;
}

export const getUserRetailer = createSelector(getUserData, user => user.retailer ?? {});
export const getUserRetailerContact = createSelector(getUserData, getUserRetailer, (user, retailer) => {
  if (user.retailercontact) {
    return user.retailercontact;
  }

  if (user.is_staff || user.is_super) {
    return retailer.contacts?.[0] ?? {};
  }

  return {};
});

export const getRetailerSalesChannels = createSelector(getUserRetailer, retailer => retailer.sales_channels ?? []);

export const getRetailerHasSalesChannels = createSelector(getRetailerSalesChannels, channels => channels.length > 0);

/**
 * @return { first_name?: string, last_name?: string }
 */
export const getUserNames = createSelector(getUserData, getUserRetailerContact, (user, contact) =>
  userNames(user, contact)
);

export const getUserRetailerIsShopify = createSelector(getUserData, user => user.shopify ?? false);

export const getUserRetailerIngestionSource = createSelector(
  getUserData,
  // eslint-disable-next-line consistent-return
  (user): IIngestionSourceData => {
    const integrations = Array.isArray(user.integrations) ? user.integrations : [];
    for (const integration of integrations) {
      if (integration.name in INGESTION_SOURCE_DATA_MAP) {
        return INGESTION_SOURCE_DATA_MAP[integration.name];
      }
    }
    return INGESTION_SOURCE_DATA_MAP[MANUAL_SOURCE];
  }
)

export const getUserOrderForwardingSettings = createSelector(
  getUserData,
 (user) => {
    return { orderForwardingEnabled: user.retailer?.order_forwarding_opted_in };
  }
);

export const getUserRetailerIsShellAccount = createSelector(
  getUserData,
  user => Boolean(user?.is_shell_account) || Boolean(user?.retailer?.onboarding_required)
);
export function getUserRetailerId(state) {
  return getUserRetailer(state)?.id;
}

export function getUserRetailerName(state) {
  return getUserRetailer(state)?.business_name;
}

export function getUserRetailerSalesChannel(state) {
  return getUserRetailer(state)?.buyer_for_sales_channel;
}

export function getUserIsStaff(state) {
  return Boolean(getUserData(state)?.is_staff);
}

export function getUserSellerType(state) {
  return getUserData(state).sellerType || {};
}

export function getUserForSalesChannelIsInternational(state) {
  const retailer = getUserRetailer(state);

  const { buyer_for_sales_channel } = retailer;
  if (!buyer_for_sales_channel) return false;

  return INTERNATIONAL_SHIPPING_RETAILERS_NAMES.includes(buyer_for_sales_channel);
}

export const isAdminUser = createSelector(getUserData, user => Boolean(user?.is_super || user?.is_staff));

export const getUserPayoutAmount = createSelector(getUserData, user => user.amount_owed);